/* eslint-disable no-debugger */
/* eslint-disable no-useless-escape */
import * as Yup from 'yup';
import { useState } from 'react';
import { useSnackbar } from 'notistack5';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { LoadingButton } from '@material-ui/lab';
import {
  Box,
  Stack,
  TextField,
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Alert
} from '@material-ui/core';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import { MIconButton } from '../../@material-extend';
// ----------------------------------------------------------------------

export default function ChangePassword() {
  const { updateProfile } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState({
    oldPass: false,
    newPass: false,
    cnfPass: false
  });

  const NewUserSchema = Yup.object().shape({
    oldPassword: Yup.string().required('Old Password is required'),
    newPassword: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
        'Password must contain atleast 8 characters that includes one Uppercase letter, one Lowercase letter, one Numeric character and one Special character'
      )
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Password does not match')
      .required('Phone number is required')
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      notification: true
    },
    validationSchema: NewUserSchema,
    onSubmit: async (values, { setSubmitting, resetForm, setErrors }) => {
      try {
        const value = {
          old_password: values.oldPassword,
          new_password: values.newPassword,
          confirm_password: values.confirmPassword,
          is_notification: values.notification
        };
        await updateProfile(value);
        enqueueSnackbar('Password Changed Successfully', {
          variant: 'success',
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        if (isMountedRef.current) {
          setSubmitting(false);
          resetForm();
        }
      } catch (error) {
        const { message } = error.response.data;
        resetForm();
        console.error(message);
        if (isMountedRef.current) {
          setSubmitting(false);
          setErrors({ afterSubmit: message });
        }
      }
    }
  });

  const { errors, values, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack spacing={{ xs: 3, sm: 2 }} direction={{ xs: 'row', sm: 'column' }}>
              <Stack sx={{ mb: 2, mt: 2 }} direction="row" spacing={{ xs: 3, sm: 2 }}>
                <Typography variant="h5" color="primary">
                  Profile Setting
                </Typography>
              </Stack>

              <Stack width="50%" spacing={{ xs: 3, sm: 2 }} direction="column">
                {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}

                <TextField
                  required
                  fullWidth
                  label="Old Password"
                  type={showPassword?.oldPass ? 'text' : 'password'}
                  {...getFieldProps('oldPassword')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ position: 'absolute', right: '15px' }}>
                        <IconButton
                          onClick={() => setShowPassword({ ...showPassword, oldPass: !showPassword.oldPass })}
                          edge="end"
                        >
                          <Icon icon={showPassword?.oldPass ? eyeFill : eyeOffFill} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    style: {
                      paddingRight: '0px',
                      position: 'relative'
                    }
                  }}
                  error={Boolean(touched.oldPassword && errors.oldPassword)}
                  helperText={touched.oldPassword && errors.oldPassword}
                />
                <TextField
                  required
                  fullWidth
                  label="New Password"
                  type={showPassword?.newPass ? 'text' : 'password'}
                  {...getFieldProps('newPassword')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ position: 'absolute', right: '15px' }}>
                        <IconButton
                          onClick={() => setShowPassword({ ...showPassword, newPass: !showPassword.newPass })}
                          edge="end"
                        >
                          <Icon icon={showPassword?.newPass ? eyeFill : eyeOffFill} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    style: {
                      paddingRight: '0px',
                      position: 'relative'
                    }
                  }}
                  error={Boolean(touched.newPassword && errors.newPassword)}
                  helperText={touched.newPassword && errors.newPassword}
                />
                <TextField
                  required
                  fullWidth
                  label="Confirm Password"
                  type={showPassword?.cnfPass ? 'text' : 'password'}
                  {...getFieldProps('confirmPassword')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ position: 'absolute', right: '15px' }}>
                        <IconButton
                          onClick={() => setShowPassword({ ...showPassword, cnfPass: !showPassword.cnfPass })}
                          edge="end"
                        >
                          <Icon icon={showPassword?.cnfPass ? eyeFill : eyeOffFill} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    style: {
                      paddingRight: '0px',
                      position: 'relative'
                    }
                  }}
                  error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                />
              </Stack>
              <Stack width="50%" spacing={{ xs: 3, sm: 2 }} direction="row">
                <FormControlLabel
                  control={<Checkbox {...getFieldProps('notification')} checked={values.notification} />}
                  label={<Typography variant="button">Recieve Notication On Mail</Typography>}
                />
              </Stack>
            </Stack>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                Save Changes
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  );
}
